import React, {useCallback, useMemo, useState} from 'react';
import {ReactComponent as ReminderIcon} from '@icons/subscription-expiry-reminder.svg';
import {ReactComponent as CloseIcon} from '@icons/Close.svg';
import {formatAmount, formatDateRuLong, pluralDays} from '@utils/tariffFormat';
import {getCalendarDaysUntil} from '@utils/subscriptionExpiryBanner';
import {useSubscriptionExpiryBanner} from '@hooks/useSubscriptionExpiryBanner';
import {useCanManageTariff} from '@hooks/useCanManageTariff';
import {Modal} from '../Modal';
import {RenewSubscriptionModal} from '../../pages/Subscription/components/RenewSubscriptionModal';
import styles from './SubscriptionExpiryBanner.module.scss';

export const SubscriptionExpiryBanner = () => {
    const {visible, endsAt, active, dismiss} = useSubscriptionExpiryBanner();
    const {canManageTariff: canManage} = useCanManageTariff();
    const [renewModalOpen, setRenewModalOpen] = useState(false);

    const daysLeft = useMemo(
        () => (endsAt ? getCalendarDaysUntil(endsAt) : Number.NaN),
        [endsAt],
    );

    const handleOpenRenew = useCallback(() => {
        if (!active || !canManage) {
            return;
        }

        setRenewModalOpen(true);
    }, [active, canManage]);

    const handleCloseRenew = useCallback(() => {
        setRenewModalOpen(false);
    }, []);

    if (!visible || !endsAt || !active) {
        return null;
    }

    const formattedDate = formatDateRuLong(endsAt);
    const expirySecondary = Number.isNaN(daysLeft)
        ? `Дата истечения: ${formattedDate}`
        : `Дата истечения: ${formattedDate} · ${daysLeft} ${pluralDays(daysLeft)}`;

    return (
        <>
            <div className={styles.banner} role="status" aria-live="polite">
                <div className={styles.inner}>
                    <div className={styles.content}>
                        <div className={styles.reminder}>
                            <ReminderIcon className={styles.icon} aria-hidden/>
                            <div className={styles.reminderText}>
                                {/* Исправление: «Скоро истекает подписка» — жирный заголовок вместо «Напоминание» */}
                                <p className={styles.title}>Скоро истекает подписка</p>
                                <p className={styles.secondary}>{expirySecondary}</p>
                            </div>
                        </div>
                    </div>

                    <div className={styles.actions}>
                        {canManage ? (
                            <button
                                type="button"
                                className={styles.renew}
                                onClick={handleOpenRenew}
                            >
                                Продлить
                            </button>
                        ) : null}

                        <button
                            type="button"
                            className={styles.close}
                            onClick={dismiss}
                            aria-label="Закрыть напоминание"
                        >
                            <CloseIcon aria-hidden/>
                        </button>
                    </div>
                </div>
            </div>

            {canManage ? (
                <Modal open={renewModalOpen} handleOpen={handleCloseRenew}>
                    <RenewSubscriptionModal
                        variant={active.rate.type !== 'basic' ? 'business' : 'basic'}
                        tariffId={active.rate.id}
                        periodDays={active.rate.duration_days}
                        userCount={active.rate.users_count}
                        price={formatAmount(active.rate.price)}
                        displayName={active.rate.display_name}
                        onClose={handleCloseRenew}
                    />
                </Modal>
            ) : null}
        </>
    );
};
